/**
 * Typed API error with status code and error code for consistent error handling.
 *
 * When the backend returns a 400 with `detail` as an object (DRF validation),
 * `fieldErrors` carries the per-field messages and callers can map them onto
 * form fields via `applyServerErrors()`.
 *
 * @example
 * catch (err) {
 *   if (err instanceof ApiError) {
 *     console.log(err.status, err.code, err.message, err.fieldErrors);
 *   }
 * }
 */
export class ApiError extends Error {
  status: number;
  code: string;
  /** Per-field validation messages from DRF when `detail` is an object (400 "invalid"). */
  fieldErrors?: Record<string, string[]>;
  /** Top-level non-field message when `detail` is a plain string. */
  formError?: string;
  /** Seconds to wait before retrying — present on 429 rate-limit responses. */
  retryAfter?: number;
  /** Raw response body (camelCased) — carries extra fields like `titleId` on 409s. */
  data?: Record<string, unknown>;

  constructor(
    status: number,
    code: string,
    message: string,
    opts?: {
      fieldErrors?: Record<string, string[]>;
      formError?: string;
      retryAfter?: number;
      data?: Record<string, unknown>;
    },
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fieldErrors = opts?.fieldErrors;
    this.formError = opts?.formError;
    this.retryAfter = opts?.retryAfter;
    this.data = opts?.data;
  }
}

/**
 * Axios client for the Ur-Nammu API.
 *
 * Base URL from env, JWT auth header injection,
 * and a 401 interceptor that attempts token refresh then redirects to /login.
 *
 * CSRF: DRF uses JWT-only auth (no SessionAuthentication) for all /api/v1/ routes.
 * No X-CSRFToken header is needed — withCredentials is for the refresh cookie only.
 *
 * Trailing slash: stripped here because backend has APPEND_SLASH=False. Do not remove.
 */
import axios from "axios";
import i18n from "@/i18n";
import { getInMemoryToken, setInMemoryToken, clearInMemoryToken, clearSessionHint } from './tokenStore';
import { toast } from './toast';
import { queryClient } from './queryClient';
import { billingKeys } from './queryKeys';
import { BILLING } from './apiPaths';
import { withBase } from './basePath';

// Extend Axios config to allow custom request flags without casting.
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean;
    _skipRefresh?: boolean;
  }
  interface AxiosRequestConfig {
    _skipRefresh?: boolean;
  }
}

// Fail loud in production when VITE_API_URL is missing — silent localhost fallback is a data breach risk.
if (import.meta.env.PROD && !import.meta.env.VITE_API_URL) {
  throw new Error('[apiClient] VITE_API_URL is required in production — set it in your deployment environment.');
}

/** Queues pending requests while a token refresh is in flight. */
let isRefreshing = false;
/** Pending request callbacks to invoke after token refresh completes. */
let refreshQueue: Array<(token: string) => void> = [];

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? 'http://localhost:8000',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send refresh cookie on cross-origin requests
});

/** Endpoints that don't need a token — don't inject Authorization for these. */
const NO_TOKEN_PATHS = ["/api/v1/auth/login", "/api/v1/auth/register", "/api/v1/auth/token/refresh"];

/** Endpoints that should NOT receive an auto-generated Idempotency-Key.
 *  Refresh rotates tokens on each call — replaying a refresh blacklists an already-blacklisted token. */
const NO_IDEMPOTENCY_PATHS = ["/api/v1/auth/token/refresh"];

/** Mutating HTTP verbs that backend's IdempotencyMixin protects. */
const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

/**
 * Generate a stable Idempotency-Key (UUID v4) that can be reused across retries
 * of the SAME logical mutation. Call once per user-initiated action and pass
 * via `{ headers: { 'Idempotency-Key': key } }` to the axios method.
 *
 * Flow:
 *  1. User clicks Submit → factory calls `createIdempotencyKey()` once.
 *  2. axios POST sent with that key in headers.
 *  3. If network fails and a retry layer re-fires the same request, backend
 *     returns the cached response with `Idempotent-Replay: true` instead of
 *     creating a duplicate side effect.
 *
 * When no caller-supplied key exists, the request interceptor below mints a
 * fresh one per HTTP attempt — fine for most cases but loses replay safety
 * across retries.
 */
export function createIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** Request interceptor: add Authorization header from in-memory store (skip auth endpoints). */
apiClient.interceptors.request.use((config) => {
  config.headers["Accept-Language"] = i18n.language;
  const token = getInMemoryToken();
  const isNoTokenPath = NO_TOKEN_PATHS.some((p) => config.url?.startsWith(p));
  if (token && !isNoTokenPath) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Inject Idempotency-Key on mutating verbs unless caller already set one
  // or the endpoint opts out (e.g., token refresh).
  const method = config.method?.toLowerCase();
  const isMutating = method ? MUTATING_METHODS.has(method) : false;
  const isIdempotencyOptOut = NO_IDEMPOTENCY_PATHS.some((p) => config.url?.startsWith(p));
  const hasUserKey = Boolean(config.headers["Idempotency-Key"]);
  if (isMutating && !isIdempotencyOptOut && !hasUserKey) {
    config.headers["Idempotency-Key"] = createIdempotencyKey();
  }

  // Strip trailing slash to avoid Django APPEND_SLASH redirects (backend has APPEND_SLASH=False)
  if (config.url?.endsWith("/")) {
    config.url = config.url.replace(/\/+$/, "");
  }
  return config;
});

/** Response interceptor: error handling + token refresh. */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const original = error.config;

    // 401 → attempt token refresh → retry or redirect (skip for auth endpoints — 401 means bad credentials)
    if (error.response?.status === 401 && !original._retry && !original._skipRefresh) {
      const isAuthEndpoint = NO_TOKEN_PATHS.some((p) => original.url?.startsWith(p));
      if (isAuthEndpoint) {
        return Promise.reject(new ApiError(
          error.response?.status ?? 0,
          error.response?.data?.code ?? "UNKNOWN",
          error.response?.data?.detail ?? error.message,
          { retryAfter: error.response?.data?.retryAfter },
        ));
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshQueue.push((token: string) => {
            original.headers.Authorization = `Bearer ${token}`;
            original._retry = true;
            resolve(apiClient(original));
          });
        });
      }

      isRefreshing = true;
      original._retry = true;

      try {
        // Use apiClient (not bare axios) so Accept-Language + base config apply.
        // Relative path required — absolute URL would bypass NO_TOKEN_PATHS startsWith check.
        // _skipRefresh: true prevents this request from re-entering the 401 handler.
        const { data } = await apiClient.post<{ access: string }>(
          '/api/v1/auth/token/refresh',
          {},
          { withCredentials: true, _skipRefresh: true }
        );
        const newAccessToken = data.access;
        setInMemoryToken(newAccessToken);
        refreshQueue.forEach((cb) => cb(newAccessToken));
        refreshQueue = [];
        original.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(original);
      } catch {
        clearInMemoryToken();
        clearSessionHint();
        refreshQueue = [];
        // Always redirect — a lone failing request must not leave the user on a protected page.
        window.location.href = withBase("/login");
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    // 403 SUBSCRIPTION_REQUIRED → the caller's subscription lapsed (or never
    // started). Re-sync the cached subscription state so the route guard
    // re-derives access (redirect first-timers to pricing / flip the dashboard
    // to read-only), and surface a specific toast instead of the generic
    // "request failed" one below. This is the reactive backstop to the proactive
    // guard + disabled controls; it never retries (distinct from the 401 path).
    const subData = error.response?.data;
    if (error.response?.status === 403 && subData?.code === "subscription_required") {
      // Don't re-sync when the subscription probe itself 403s — invalidating the
      // query that just failed refetches it, which 403s again → infinite loop.
      // The query's own error state already reflects the lapsed subscription.
      const isSubscriptionProbe = original.url?.startsWith(`${BILLING}/subscription`);
      if (!isSubscriptionProbe) {
        void queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
      }
      const subDetail = typeof subData.detail === "string" ? subData.detail : undefined;
      toast.error(i18n.t("readOnly.banner"), {
        id: "subscription-required",
        description: subDetail ?? i18n.t("readOnly.disabledHint"),
      });
      return Promise.reject(new ApiError(
        403,
        subData.code ?? "subscription_required",
        subDetail ?? error.message,
        { data: typeof subData === "object" ? (subData as Record<string, unknown>) : undefined },
      ));
    }

    const data = error.response?.data;
    const detail = data?.detail;

    // Toast every 5xx — backend faults the user can't act on. Stable id collapses
    // a burst of failures into a single toast instead of stacking them.
    // Unhandled 4xx also toast, but skip 400 (field errors are mapped onto forms)
    // and 401 (handled above via silent refresh/redirect).
    // Description prefers the backend's own `detail` string when present — it's
    // localized via Accept-Language — and falls back to a default message.
    const status = error.response?.status ?? 0;
    const detailText = typeof detail === "string" ? detail : undefined;
    if (status >= 500 && status < 600) {
      toast.error(i18n.t("common.errors.serverError"), {
        id: "server-error",
        description: detailText ?? i18n.t("common.errors.serverErrorDescription"),
      });
    } else if (status >= 400 && status < 500 && status !== 400 && status !== 401 && status !== 403) {
      toast.error(i18n.t("common.errors.requestFailed"), {
        id: "client-error",
        description: detailText ?? i18n.t("common.errors.requestFailedDescription"),
      });
    }

    const isFieldObj =
      detail !== null &&
      typeof detail === "object" &&
      !Array.isArray(detail);
    return Promise.reject(new ApiError(
      status,
      data?.code ?? "UNKNOWN",
      isFieldObj ? "Validation failed" : (typeof detail === "string" ? detail : error.message),
      {
        fieldErrors: isFieldObj ? (detail as Record<string, string[]>) : undefined,
        formError: !isFieldObj && typeof detail === "string" ? detail : undefined,
        retryAfter: data?.retryAfter,
        data: data && typeof data === "object" ? (data as Record<string, unknown>) : undefined,
      },
    ));
  }
);